import log from "loglevel";
import { all, take, call, select, fork, put, takeEvery, takeLatest } from "redux-saga/effects";
import * as nanoid from "nanoid";
import { API, Auth } from "aws-amplify";
import { getCurrentCredentials } from "./CognitoHelperFunctions";
import {
  ACQUIRE_POLICIES_START,
  IOT_CONNECT_CALLBACK_START,
  IOT_CLOSE_CALLBACK_START,
  ATTACH_CONNECT_POLICY,
  ATTACH_SUBSCRIBE_POLICY,
  ATTACH_PUBLIC_RECEIVE_POLICY,
  ATTACH_PUBLIC_PUBLISH_POLICY,
  USER_PRIVILEGES_GET_SUCCESS,
  ON_NEW_MESSAGE_TRIGGERED
} from "../actions";
import {
  acquirePoliciesSuccess,
  acquirePoliciesError,
  attachConnectPolicySuccess,
  attachConnectPolicyError,
  attachSubscribePolicySuccess,
  attachSubscribePolicyError,
  attachPublicPublishPolicySuccess,
  attachPublicPublishPolicyError,
  attachPublicReceivePolicySuccess,
  attachPublicReceivePolicyError,
  addSubscribedTopic,
  setMessageHandlerStatus,
  setDeviceStatus,
  iotConnectCallbackSuccess,
  iotConnectCallbackError,
  iotCloseCallbackSuccess,
  iotCloseCallbackError,
  clearSubscribedTopics,
  addSubscribedTopicError,
  iotErrorOccurred,
  setIotClientId
} from "./actions";
import { getBookingListDelta } from "../booking/actions";
import {
  getRequestListDelta, getAlacarteRequestList, getAlacarteRequestById
} from "../request/actions";
import { getRequestRestaurant } from "../alacarte/actions";
import * as IoT from "./aws-iot";
import { forceReload } from "../common/actions";
import { getServiceList } from "../service/actions";

let config;
let mergedConfig;

const REQUEST_TOPIC = "request";
const BOOKING_TOPIC = "booking";
const LOG_TOPIC = "log";
const PAYMENTS_TOPIC = "payments";
const ALACARTE_CONFIG_TOPIC = "alacarteConfig";

let insertAudio;

const playInsertAudio = async () => {
  if (typeof window !== "undefined") {
    const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
    const { attributes } = user;
    const mp3 = attributes["custom:ringTone"] || "doorbell";
    insertAudio = new Audio(`/assets/sounds/${mp3}.mp3`);
  }
  if (insertAudio && insertAudio.play) {
    insertAudio.muted = false;
    insertAudio
      .play()
      .then(() => {
        setTimeout(() => {
          insertAudio.muted = true; // After playing, turn on the mute state
        }, 5000);
      })
      .catch(err => console.error("audio err", err));
  }
};
/**
 * Handler for incoming MQTT messages
 *
 * @returns {function}  The wrapped onNewMessage handler
 *
 * onNewMessage handler parses message and forwards to newMessage action
 * @params {string} topic - The topic that the message was published to
 * @params {string} jsonPayload - JSON payload of the message
 */
function* onNewMessageWithRedux({ payload }) {
  try {
    if (payload && Object.keys(payload).length) {
      const { payload: { message }, topic } = payload;
      let mqttBaseTopic = yield select(state => state.auth.userPrivileges.mqttBaseTopic);
      if (!mqttBaseTopic) {
        yield take(USER_PRIVILEGES_GET_SUCCESS);
        mqttBaseTopic = yield select(state => state.auth.userPrivileges.mqttBaseTopic);
      }
      const { allAlacarteItems } = yield select(state => state.alacarteApp);
      switch (topic) {
        case `${mqttBaseTopic}/${REQUEST_TOPIC}`: {
          const request = message.data;
          if (message.eventName === "MODIFY") {
            if (request.comments) {
              if (request.is_commented_by === "GUEST") {
                playInsertAudio();
              }
            }
            if (request.is_canceled === true) {
              playInsertAudio();
            }
          } else if (message.eventName === "INSERT") {
            playInsertAudio();
          }
          if (message?.data?.service_type === "ALACARTE_RESTAURANT_RESERVATION") {
            const alacarteReqId = message?.data?.id;
            if (alacarteReqId) yield put(getAlacarteRequestById(alacarteReqId));
          } else {
            yield put(getRequestListDelta());
          }
          break;
        }
        case `${mqttBaseTopic}/${BOOKING_TOPIC}`: {
          if (message.eventName === "INSERT") {
            playInsertAudio();
          }
          yield put(getBookingListDelta());   // bookingsDelta
          break;
        }
        case `${mqttBaseTopic}/${LOG_TOPIC}`: {
          if (message.eventName === "INSERT") {
            playInsertAudio();
          }
          break;
        }
        case `${mqttBaseTopic}/${ALACARTE_CONFIG_TOPIC}`: {
          yield put(getRequestRestaurant({ allAlacarteItems }));   // alacarteDelta
          break;
        }
        /**
         * { "message": { "eventName":"FORCE_RELOAD", "data":{ "isHard":true } } }
         */
        case "all": {
          // const updateData = message.data;
          if (message.eventName === "FORCE_RELOAD") {
            yield put(forceReload());
          }
          // TODO:::
          // yield put(forceReload(updateData));
          break;
        }
        default:
          break;
      }
      // newMessage(message, topic)(dispatch, getState);
    } else {
      log.error("payload is empty");
    }
  } catch (err) {
    console.error(err);
  }
}

export function* attachMessageHandler() {
  const attached = yield select(state => state.iot.messageHandlerAttached);
  try {
    if (!attached) {
      IoT.attachMessageHandler();
    }
    yield put(setMessageHandlerStatus(true));
  } catch (error) {
    // alert(error);
    yield put(iotErrorOccurred(`AttachMessageHandler err: ${error.message}`));
  }
}

export const attachConnectPolicyAsync = async api => {
  const apiName = "WebeePolicies";
  const path = "/utilities/attach_connect2";
  const myInit = {
    body: {},
    timeout: 20 * 1000
  };
  try {
    const result = await api.post(apiName, path, myInit);
    return result;
  } catch (err) {
    throw err;
  }
};

export function* attachConnectPolicy(api = API) {
  try {
    const attachPolicy = yield call(attachConnectPolicyAsync, api);
    yield put(attachConnectPolicySuccess(attachPolicy));
  } catch (error) {
    yield put(attachConnectPolicyError(error));
  }
}

export function* watchAttachConnectPolicy() {
  yield takeLatest(ATTACH_CONNECT_POLICY, attachConnectPolicy);
}

export const attachPublicPublishPolicyAsync = async api => {
  try {
    const apiName = "WebeePolicies";
    const path = "/utilities/attach_public_publish2";

    const myInit = {
      body: {},
      timeout: 20 * 1000
    };
    const result = await api.post(apiName, path, myInit);
    return result;
  } catch (err) {
    throw err;
  }
};

export function* attachPublicPublishPolicy(api = API) {
  try {
    const attachPolicy = yield call(attachPublicPublishPolicyAsync, api);
    yield put(attachPublicPublishPolicySuccess(attachPolicy));
  } catch (error) {
    yield put(attachPublicPublishPolicyError(error));
  }
}

export function* watchAttachPublicPublishPolicy() {
  yield takeLatest(ATTACH_PUBLIC_PUBLISH_POLICY, attachPublicPublishPolicy);
}

export const attachSubscribePolicyAsync = async (appId, hotelId) => {
  const apiName = "WebeePolicies";
  const path = "/utilities/attach_public_subscribe2";
  const myInit = {
    body: { app_id: "staff", hotel_id: hotelId },
    timeout: 20 * 1000
  };
  try {
    const result = await API.post(apiName, path, myInit);
    return result;
  } catch (err) {
    throw err;
  }
};

export function* attachSubscribePolicy({ payload }) {
  try {
    const { appId = "staff", hotelId } = payload;
    const attachPolicy = yield call(attachSubscribePolicyAsync, appId, hotelId);
    yield put(attachSubscribePolicySuccess(attachPolicy));
  } catch (error) {
    yield put(attachSubscribePolicyError(error));
  }
}

export function* watchAttachSubscribePolicy() {
  yield takeLatest(ATTACH_SUBSCRIBE_POLICY, attachSubscribePolicy);
}

export const attachPublicReceivePolicyAsync = async () => {
  const apiName = "WebeePolicies";
  const path = "/utilities/attach_public_receive2";

  try {
    const result = await API.post(apiName, path, { timeout: 20 * 1000 });
    return result;
  } catch (err) {
    throw err;
  }
};

export function* attachPublicReceivePolicy() {
  try {
    const attachPolicy = yield call(attachPublicReceivePolicyAsync);
    yield put(attachPublicReceivePolicySuccess(attachPolicy));
  } catch (error) {
    yield put(attachPublicReceivePolicyError(error));
  }
}

export function* watchAttachPublicReceivePolicy() {
  yield takeLatest(ATTACH_PUBLIC_RECEIVE_POLICY, attachPublicReceivePolicy);
}

/**
 * Subscribe to topic if we have not yet subscribed
 *
 * @param {string} topic - The topic that we want to subscribe to
 */
export function* subscribeToTopic(topic) {
  const { subscribedTopics } = yield select(state => state.iot);
  if (subscribedTopics.includes(topic)) {
    log.info("Already subscribed to topic", topic);
  } else {
    try {
      IoT.subscribe(topic);
      yield put(addSubscribedTopic(topic));
    } catch (err) {
      log.error("failed to subscribe to topic: ", err);
      yield put(addSubscribedTopicError(err.message));
    }
  }
}

export function* connectCallbackSaga({ payload }) {
  try {
    const { connack } = payload;
    let { mqttBaseTopic } = yield select(state => state.auth.userPrivileges);
    // TODO if userPrivileges does not exist, call getUserPrivileges
    // yield call(getUserPrivileges, appId, this.props.hotelId);
    if (!mqttBaseTopic) {
      yield take(USER_PRIVILEGES_GET_SUCCESS);
      mqttBaseTopic = yield select(state => state.auth.userPrivileges.mqttBaseTopic);
    }

    const requestTopicToSubscribe = `${mqttBaseTopic}/${REQUEST_TOPIC}`;
    const bookingTopicToSubscribe = `${mqttBaseTopic}/${BOOKING_TOPIC}`;
    const logTopicToSubscribe = `${mqttBaseTopic}/${LOG_TOPIC}`;
    const paymentsTopicToSubscribe = `${mqttBaseTopic}/${PAYMENTS_TOPIC}`;
    const alacarteConfigTopicToSubscribe = `${mqttBaseTopic}/${ALACARTE_CONFIG_TOPIC}`;

    const publicTopic = "all";
    try {
      yield call(subscribeToTopic, requestTopicToSubscribe);
      yield call(subscribeToTopic, bookingTopicToSubscribe);
      yield call(logTopicToSubscribe, logTopicToSubscribe);
      yield call(subscribeToTopic, paymentsTopicToSubscribe);
      yield call(subscribeToTopic, alacarteConfigTopicToSubscribe);
      yield call(subscribeToTopic, publicTopic);
    } catch (error) {
      yield put(addSubscribedTopicError(error.message));
    }

    if (!connack.sessionPresent) {
      yield put(setMessageHandlerStatus(false));
      try {
        yield call(attachMessageHandler);
        // TODO Ping to test connection

        const { allAlacarteItems } = yield select(state => state.alacarteApp);
        const { allServiceItems } = yield select(state => state.requestApp);

        // TODO HANDLE NETWORK ERROR!!!!!!!
        yield put(getRequestListDelta());   // requestsDelta
        yield put(getAlacarteRequestList());   // alacarteRequests
        yield put(getBookingListDelta());   // bookingsDelta
        yield put(getRequestRestaurant({ allAlacarteItems }));   // alacarteDelta
        yield put(getServiceList({ allServiceItems }));   // services

        yield put(setDeviceStatus("connected"));
      } catch (err) {
        log.error(err);
        yield put(iotConnectCallbackError(`ConnectCallbackError1: ${err.message}`));
      }
    } else {
      log.error("Session present");
    }

    yield put(iotConnectCallbackSuccess());
  } catch (error) {
    yield put(iotConnectCallbackError(`ConnectCallbackError: ${error.message}`));
  }
}

export function* initIotClientSaga() {
  yield call(IoT.initNewClient, mergedConfig);
  IoT.attachConnectHandler();
  IoT.attachReconnectHandler();
  IoT.attachOfflineHandler();
  IoT.attachErrorHandler();
  IoT.attachCloseHandler();
  yield call(attachMessageHandler);
}
/**
 * Ask API Gateway for Iot policy
 * 1. Ensure we are logged in
 * 2. Fetch AWS credentials from sessionStorage, identityId and attach to a new MQTT client
 * 3. Ask for Connect, Publish, Subscribe, Receive policies
 */

export function* acquirePoliciesSaga(isAfterError = false) {
  try {
    const { connectPolicy, publicPublishPolicy, subscribePolicy, publicReceivePolicy, deviceStatus } = yield select(state => state.iot);
    let { clientId } = yield select(state => state.iot);
    const { sub } = yield select(state => (state && state.auth && state.auth.profile ? state.auth.profile.sub : "unknown-user"));
    const { subscribedTopics } = yield select(state => state.iot);
    const { hotelConfig } = yield select(state => state.settings);
    const appId = "staff";
    const { hotelId } = hotelConfig;
    let awsCredentials;
    if (typeof window !== "undefined") {
      awsCredentials = yield call(getCurrentCredentials);
    }
    if (
      awsCredentials &&
      (!config ||
        deviceStatus !== "connected" ||
        !clientId ||
        !mergedConfig ||
        !connectPolicy ||
        !subscribePolicy ||
        !publicPublishPolicy ||
        !publicReceivePolicy ||
        isAfterError ||
        !subscribedTopics ||
        !subscribedTopics.length)
    ) {
      config = hotelConfig;
      if (!clientId || isAfterError) {
        yield put(setIotClientId(`${hotelId}-${sub}-${nanoid()}`));
      }
      clientId = yield select(state => state.iot.clientId);
      mergedConfig = { ...config, ...awsCredentials, clientId };

      if (!connectPolicy) {
        yield call(attachConnectPolicy);
      }
      if (!publicReceivePolicy) {
        yield call(attachPublicReceivePolicy);
      }
      if (!subscribePolicy) {
        yield call(attachSubscribePolicy, { payload: { appId, hotelId } });
      }

      if (!publicPublishPolicy) {
        yield call(attachPublicPublishPolicy);
      }

      yield call(initIotClientSaga);
      const stateAfter = yield select(state => state.iot);

      if (stateAfter.connectPolicy && stateAfter.publicReceivePolicy && stateAfter.subscribePolicy && stateAfter.publicPublishPolicy) {
        yield put(acquirePoliciesSuccess());
      } else throw new Error("Policy error");
    } else if (!awsCredentials) {
      throw new Error("Missing aws credentials");
    } else {
      yield put(acquirePoliciesSuccess());
    }
  } catch (err) {
    log.error(`'iotErr${err}`);
    yield put(acquirePoliciesError(err));
  }
}

export function* closeCallbackSaga() {
  try {
    // const subscribedTopics = yield select(state => state.iot.subscribedTopics);
    yield put(setDeviceStatus("closed"));
    yield put(clearSubscribedTopics());
    yield put(setMessageHandlerStatus(false));
    yield put(iotCloseCallbackSuccess());
  } catch (error) {
    yield put(iotCloseCallbackError(error.message));
  }
}

export function* watchAcquirePoliciesSaga() {
  yield takeLatest(ACQUIRE_POLICIES_START, acquirePoliciesSaga);
}

export function* watchConnectCallbackSaga() {
  yield takeLatest(IOT_CONNECT_CALLBACK_START, connectCallbackSaga);
}

export function* watchCloseCallbackSaga() {
  yield takeLatest(IOT_CLOSE_CALLBACK_START, closeCallbackSaga);
}

export function* watchOnNewMessageWithRedux() {
  yield takeEvery(ON_NEW_MESSAGE_TRIGGERED, onNewMessageWithRedux);
}
export default function* rootSaga() {
  yield all([
    fork(watchAttachConnectPolicy),
    fork(watchAttachPublicReceivePolicy),
    fork(watchAttachSubscribePolicy),
    fork(watchAttachPublicPublishPolicy),
    fork(watchConnectCallbackSaga),
    fork(watchCloseCallbackSaga),
    fork(watchAcquirePoliciesSaga),
    fork(watchOnNewMessageWithRedux)
  ]);
}
